import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, Truck, Clock, Route, FileText, AlertOctagon, Receipt } from "lucide-react";
import { usePermissions } from "@/contexts/PermissionsContext";

const ACTIONS = [
  { label: "Mitarbeiter", to: "/mitarbeiter?new=1", icon: Users, kbd: "M", testid: "qa-mitarbeiter", module: "mitarbeiter" },
  { label: "Fahrzeug", to: "/fuhrpark?new=1", icon: Truck, kbd: "F", testid: "qa-fahrzeug", module: "fuhrpark" },
  { label: "Zeiterfassung", to: "/operations/zeiterfassung?new=1", icon: Clock, kbd: "Z", testid: "qa-zeiterfassung", module: "operations" },
  { label: "Lenkprotokoll", to: "/operations/lenkprotokoll?new=1", icon: Route, kbd: "L", testid: "qa-lenkprotokoll", module: "operations" },
  { label: "Dokument", to: "/dokumente?new=1", icon: FileText, kbd: "D", testid: "qa-dokument", module: "dokumente" },
  { label: "Schaden", to: "/fuhrpark?newDamage=1", icon: AlertOctagon, kbd: "S", testid: "qa-schaden", module: "fuhrpark" },
  { label: "Rechnung", to: "/finanzen?tab=rechnungen&new=1", icon: Receipt, kbd: "R", testid: "qa-rechnung", module: "finanzen" },
];

export default function QuickActionMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const { can } = usePermissions();

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);

  function go(action) {
    setOpen(false);
    navigate(action.to);
  }

  const visible = ACTIONS.filter((a) => can(a.module, "create"));
  if (visible.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Schnellaktion (⌘⇧N)"
        data-testid="quick-action-trigger"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${open ? "bg-blue-600 text-white" : "bg-zinc-800 hover:bg-blue-600 text-zinc-100 hover:text-white"}`}
      >
        <Plus className="w-4 h-4" />
        <span className="hidden md:inline">Neu</span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-72 bg-zinc-900 border border-zinc-800 rounded-md shadow-2xl shadow-black/50 z-50 overflow-hidden"
          data-testid="quick-action-menu"
        >
          <div className="px-3 py-2 border-b border-zinc-800 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold flex items-center justify-between">
            <span>Schnellaktion</span>
            <span className="text-zinc-600 font-mono">⌘⇧N</span>
          </div>
          <ul className="py-1">
            {visible.map((a) => (
              <li key={a.label}>
                <button
                  onClick={() => go(a)}
                  data-testid={a.testid}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-200 hover:bg-blue-600 hover:text-white transition-colors text-left group"
                >
                  <a.icon className="w-4 h-4 text-zinc-500 group-hover:text-white" />
                  <span className="flex-1">{a.label}</span>
                  <span className="text-[10px] font-mono text-zinc-600 group-hover:text-blue-100 px-1.5 py-0.5 bg-zinc-950 group-hover:bg-blue-700 border border-zinc-800 group-hover:border-blue-500 rounded">
                    {a.kbd}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
